/**
 * EntityValidator - JSON Schema validation for monsters and characters
 * 
 * Provides validation utilities using AJV for ensuring data integrity
 * across the application. Validates entities on load/save operations.
 */

import Ajv from "ajv";
import addFormats from "ajv-formats";
import { CREATURE_ACTION_JSON_FIELDS, CREATURE_DATA_JSON_FIELDS } from "../../../shared/types/CanonicalSchemas";
import { validateCreatureInventory } from "../../../shared/rules/creatureAbilities";

// Import schemas - using require for JSON files in older AJV version
// date_of_change: 2025-09-01 - Using standard schemas from AGENTS.md
const monsterSchema = require("../schemas/monster_template.json");
const characterSchema = require("../schemas/master_character_schema.json");

// DEBUG:VALIDATION
const DEBUG_ENABLED = typeof process !== "undefined"
  ? !!process.env.DEBUG_WINDSURF
  : (typeof window !== "undefined" && window.DEBUG_WINDSURF === true);

function dlog(tag, ...args) {
  if (DEBUG_ENABLED) console.debug(`[DEBUG:${tag}]`, ...args);
}

// Initialize AJV with the canonical schema formats retained across the AJV 8 upgrade.
// date_of_change: 2025-12-04 - Remove unknown props to satisfy additionalProperties:false across nested objects
// Validation must never delete mechanics from the caller. AGENTS.md; date_of_change: 2026-09-05.
Object.assign(monsterSchema.properties.data.properties, CREATURE_DATA_JSON_FIELDS);
Object.assign(monsterSchema.definitions.action.properties, CREATURE_ACTION_JSON_FIELDS);
Object.assign(monsterSchema.definitions.feature.properties, CREATURE_ACTION_JSON_FIELDS);
const ajv = new Ajv({ allErrors: true, strict: false, removeAdditional: false });
// WHAT/WHY: AJV 8 moved RFC date-time validation into ajv-formats. Register it
// explicitly so canonical metadata keeps the same validated contract instead
// of silently accepting unchecked date strings. date_of_change: 2026-09-14
addFormats(ajv);

// Compile validators
const validateMonsterSchema = ajv.compile(monsterSchema);
const validateCharacterSchema = ajv.compile(characterSchema);

/**
 * Validates an entity against its schema
 * @param {Object} entity - The entity to validate
 * @returns {Object} - { ok: boolean, errors: Array, entityType: string }
 */
export function validateEntity(entity) {
  if (!entity || typeof entity !== 'object') {
    return {
      ok: false,
      errors: [{ message: "Entity must be a valid object" }],
      entityType: "unknown"
    };
  }

  let validate;
  let entityType = entity.type;

  // date_of_change: 2025-12-04
  // rule_reference: AGENTS.md SSOT; tolerate root wrapper drift while keeping strict data.*
  // Always validate monsters using a minimal wrapper { type, data } to avoid root-level
  // additionalProperties errors on older schemas that disallow extra top-level fields.
  const candidate = (entity && entity.type === 'monster')
    ? { type: 'monster', data: entity.data }
    : entity;

  // date_of_change: 2025-10-19
  // rule_reference: AGENTS.md §9 NO LEGACY PATHS OR FALLBACKS; Strict validation gates
  // Enforce canonical-only character entities. Reject legacy-flat shapes that omit the canonical `data` node
  // even though `master_character_schema.json` supports a legacy oneOf branch for migration.
  if (entity.type === 'character') {
    const hasCanonicalData = entity && typeof entity.data === 'object' && entity.data !== null;
    if (!hasCanonicalData) {
      return {
        ok: false,
        errors: [{ message: 'Missing required field: data (canonical character entity required)' }],
        entityType: 'character'
      };
    }
  }

  switch (candidate.type) {
    case "monster":
      validate = validateMonsterSchema;
      break;
    case "character":
      validate = validateCharacterSchema;
      break;
    default:
      return {
        ok: false,
        errors: [{ message: `Unknown entity type: ${candidate.type}` }],
        entityType: candidate.type || "unknown"
      };
  }

  if (candidate.type === 'monster' && candidate.data?.contentVersion) {
    try { validateCreatureInventory(candidate.data, entity.id); }
    catch (error) { return { ok: false, errors: [{ message: error.message, dataPath: error.path, code: error.code }], entityType }; }
  }
  let ok = validate(candidate);
  let errors = validate.errors || [];

  // date_of_change: 2025-12-04
  // rule_reference: AGENTS.md SSOT; tolerate wrapper/metadata drift while keeping strict data.*
  // Some environments still load older monster schemas with additionalProperties:false at the root
  // and no awareness of newer wrapper keys. We treat *root-level* additionalProperties on monsters
  // as non-fatal wrapper drift and keep enforcing strict validation inside data.*.
  if (candidate && candidate.type === 'monster' && Array.isArray(errors) && errors.length) {
    const ignoredRootAdditional = [];
    errors = errors.filter((e) => {
      const keyword = e && e.keyword;
      const path = (e && (e.instancePath || e.dataPath)) || '';
      if ((keyword === 'additionalProperties' || keyword === 'unevaluatedProperties') && (!path || path === '')) {
        ignoredRootAdditional.push(e);
        return false;
      }
      return true;
    });

    if (ignoredRootAdditional.length) {
      dlog('VALIDATION', 'Ignoring root-level additionalProperties for monster wrapper', {
        rootKeys: Object.keys(candidate || {}),
        ignored: ignoredRootAdditional
      });
    }

    if (!errors.length && ignoredRootAdditional.length) {
      ok = true;
    }
  }

  // DEBUG:VALIDATION
  if (!ok) {
    dlog("VALIDATION", `Validation failed for ${entityType}:`, errors);
  } else {
    dlog("VALIDATION", `Validation passed for ${entityType}: ${candidate.data?.name || candidate.id}`);
  }

  return { ok, errors, entityType };
}

/**
 * Validates a monster specifically
 * @param {Object} monster - Monster entity
 * @returns {Object} - Validation result
 */
export function validateMonster(monster) {
  return validateEntity({ ...monster, type: "monster" });
}

/**
 * Validates a character specifically
 * @param {Object} character - Character entity
 * @returns {Object} - Validation result
 */
export function validateCharacter(character) {
  return validateEntity({ ...character, type: "character" });
}

/**
 * Creates a user-friendly error message from validation errors
 * @param {Array} errors - AJV validation errors
 * @returns {string} - Formatted error message
 */
export function formatValidationErrors(errors) {
  if (!errors || errors.length === 0) return "";
  
  return errors.map(error => {
    const path = error.instancePath || error.dataPath || "";
    const field = path.replace(/^\//, "").replace(/\//g, ".");
    
    switch (error.keyword) {
      case "required":
        return `Missing required field: ${error.params?.missingProperty || field}`;
      case "type":
        return `Invalid type for ${field}: expected ${error.params?.type}`;
      case "enum":
        return `Invalid value for ${field}: must be one of ${error.params?.allowedValues?.join(", ")}`;
      case "minimum":
        return `Value for ${field} must be at least ${error.params?.limit}`;
      case "maximum":
        return `Value for ${field} must be at most ${error.params?.limit}`;
      case "additionalProperties": {
        const extra = error.params?.additionalProperty || error.params?.additionalProperties;
        if (!field) {
          return `Unexpected property ${extra || "(unknown)"} at root`; 
        }
        return `${field}: unexpected property ${extra || "(unknown)"}`;
      }
      default:
        return `${field}: ${error.message}`;
    }
  }).join("; ");
}

const EntityValidator = {
  validateEntity,
  validateMonster,
  validateCharacter,
  formatValidationErrors
};

export default EntityValidator;
